import axios from 'axios';
import { useEffect, useMemo, useReducer } from 'react';
import { getCanonicalFormDescription } from '../pages/stock/secFormDescriptions';

const normaliseFormType = (value) => String(value || '').trim().toUpperCase();

const safeParseDate = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const normaliseSections = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(/[,;]+/).flatMap((item) => {
      const trimmed = item.trim();
      return trimmed ? [trimmed] : [];
    });
  }

  return [];
};

const normaliseFiling = (filing) => {
  if (!filing || typeof filing !== 'object') {
    return null;
  }

  const formType =
    filing.formType ||
    filing.form_type ||
    filing.form ||
    filing.type ||
    filing.document_type ||
    '—';

  const description =
    filing.description ||
    filing.formDescription ||
    filing.form_description ||
    filing.primary_doc_description ||
    filing.primaryDocumentDescription ||
    filing.primary_document_description ||
    '';

  const filingDateRaw =
    filing.filingDate ||
    filing.filing_date ||
    filing.filedAt ||
    filing.filed_at ||
    filing.acceptanceDateTime ||
    filing.acceptance_datetime ||
    filing.filingDateTime ||
    filing.filing_datetime ||
    '';

  const filingDate = filingDateRaw ? String(filingDateRaw).split('T')[0] : '';

  const reportDateRaw =
    filing.reportingDate ||
    filing.reporting_date ||
    filing.reportDate ||
    filing.report_date ||
    filing.periodOfReport ||
    filing.period_of_report ||
    '';

  const reportingDate = reportDateRaw ? String(reportDateRaw).split('T')[0] : '';

  const href =
    filing.url ||
    filing.href ||
    filing.filingUrl ||
    filing.filing_url ||
    filing.documentUrl ||
    filing.document_url ||
    filing.link ||
    null;

  const sections = normaliseSections(
    filing.sections || filing.items || filing.item || filing.topics
  );

  const aiRaw = filing.ai || filing.ai_enrichment || filing.enrichment || null;
  const masterTagsRaw = aiRaw?.master_tags ?? aiRaw?.masterTags;
  const masterTags = Array.isArray(masterTagsRaw)
    ? masterTagsRaw.flatMap((tag) => {
        const trimmed = String(tag || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];
  const aiTagsRaw = aiRaw?.tags;
  const aiTags = Array.isArray(aiTagsRaw)
    ? aiTagsRaw.flatMap((tag) => {
        const trimmed = String(tag || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];
  const aiSummary = aiRaw?.summary140 ? String(aiRaw.summary140) : '';
  const aiStatus = aiRaw?.status ? String(aiRaw.status) : null;

  return {
    id:
      filing.id ||
      filing.accessionNumber ||
      filing.accession_number ||
      filing.documentId ||
      filing.document_id ||
      `${formType}-${filingDate}-${reportingDate}`,
    formType,
    formTypeKey: normaliseFormType(formType),
    description: description || getCanonicalFormDescription(normaliseFormType(formType)),
    filingDate,
    filingDateValue: safeParseDate(filingDate),
    reportingDate,
    reportingDateValue: safeParseDate(reportingDate),
    href,
    sections,
    ai: {
      status: aiStatus,
      masterTags,
      tags: aiTags,
      summary140: aiSummary,
    },
  };
};

const normaliseFilingsPayload = (data) => {
  const filingsPayload = Array.isArray(data)
    ? data
    : data?.filings || data?.results || data?.data || [];

  return filingsPayload.flatMap((filing) => {
    const normalised = normaliseFiling(filing);
    return normalised ? [normalised] : [];
  });
};

const aiTagsKey = (tags) =>
  Array.isArray(tags)
    ? tags
        .flatMap((tag) => {
          const trimmed = String(tag || '').trim();
          return trimmed ? [trimmed] : [];
        })
        .join('|')
    : '';

const isAiEqual = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.status || null) === (b.status || null) &&
    (a.summary140 || '') === (b.summary140 || '') &&
    aiTagsKey(a.masterTags) === aiTagsKey(b.masterTags) &&
    aiTagsKey(a.tags) === aiTagsKey(b.tags)
  );
};

async function fetchSecFilingsWithAi(apiBaseUrl, ticker, signal) {
  if (signal?.aborted) {
    return [];
  }

  const endpoint = `${apiBaseUrl}/api/sec_filings?ticker=${encodeURIComponent(
    ticker
  )}&include_ai=1`;
  const { data } = await axios.get(endpoint, { signal });
  return normaliseFilingsPayload(data);
}

function mergeFilingsWithAiUpdates(prevFilings, normalised) {
  const latestById = new Map(normalised.map((filing) => [filing.id, filing]));
  return prevFilings.map((existing) => {
    const next = latestById.get(existing.id);
    if (!next) return existing;
    if (isAiEqual(existing.ai, next.ai)) {
      return existing;
    }
    return { ...existing, ai: next.ai };
  });
}

function startSecFilingsAiPolling(apiBaseUrl, ticker, onUpdate) {
  let isCancelled = false;
  const controller = new AbortController();

  const refreshAi = () => {
    if (isCancelled || controller.signal.aborted) return;

    fetchSecFilingsWithAi(apiBaseUrl, ticker, controller.signal)
      .then((normalised) => {
        if (isCancelled) return;
        onUpdate(normalised);
      })
      .catch((err) => {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
      });
  };

  refreshAi();
  const interval = setInterval(refreshAi, 4000);

  return () => {
    isCancelled = true;
    controller.abort();
    clearInterval(interval);
  };
}

function startSecFilingsFetch(apiBaseUrl, ticker, onSuccess, onError) {
  let isCancelled = false;
  const controller = new AbortController();

  fetchSecFilingsWithAi(apiBaseUrl, ticker, controller.signal)
    .then((normalised) => {
      if (isCancelled) return;
      onSuccess(normalised);
    })
    .catch((err) => {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;

      if (!isCancelled && !controller.signal.aborted) {
        onError(
          err?.response?.data?.error ||
            err?.message ||
            'Unable to load SEC filings right now.'
        );
      }
    });

  return () => {
    isCancelled = true;
    controller.abort();
  };
}

const SEC_FILINGS_INITIAL_STATE = {
  filings: [],
  isFetching: true,
  error: null,
};

function secFilingsReducer(state, action) {
  switch (action.type) {
    case 'fetch_start':
      return { filings: [], isFetching: true, error: null };
    case 'fetch_success':
      return { filings: action.filings, isFetching: false, error: null };
    case 'fetch_error':
      return {
        filings: [],
        isFetching: false,
        error: action.error,
      };
    case 'merge_ai_updates':
      return {
        ...state,
        filings: mergeFilingsWithAiUpdates(state.filings, action.normalised),
      };
    default:
      return state;
  }
}

export function useSecFilingsData(ticker, apiBaseUrl) {
  const [{ error, filings, isFetching }, dispatch] = useReducer(
    secFilingsReducer,
    SEC_FILINGS_INITIAL_STATE
  );

  useEffect(() => {
    if (!ticker) {
      return undefined;
    }

    dispatch({ type: 'fetch_start' });
    return startSecFilingsFetch(
      apiBaseUrl,
      ticker,
      (normalised) => {
        dispatch({ type: 'fetch_success', filings: normalised });
      },
      (errorMessage) => {
        dispatch({ type: 'fetch_error', error: errorMessage });
      }
    );
  }, [apiBaseUrl, ticker]);

  const hasPendingAi = useMemo(
    () => filings.some((filing) => filing?.ai?.status === 'pending'),
    [filings]
  );

  useEffect(() => {
    if (!ticker || !hasPendingAi) {
      return undefined;
    }

    return startSecFilingsAiPolling(apiBaseUrl, ticker, (normalised) => {
      dispatch({ type: 'merge_ai_updates', normalised });
    });
  }, [apiBaseUrl, hasPendingAi, ticker]);

  return { error, filings, isFetching };
}
